/** The languages the app can be shown in. The English text in the code is the key, and the fallback. */
export type Language = 'en' | 'vi';

export const LANGUAGES: readonly { code: Language; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'vi', label: 'Tiếng Việt' },
];

const DICTIONARIES: Record<Language, Record<string, string>> = { en: {}, vi: {} };

/** Hands over the words of a language (the web app fetches Vietnamese only when it is wanted; the phone app has it built in). */
export function registerDictionary(language: Language, words: Record<string, string>): void {
  DICTIONARIES[language] = words;
  patternCache.delete(language);
}

export const isLoaded = (language: Language): boolean => language === 'en' || Object.keys(DICTIONARIES[language]).length > 0;


export const isLanguage = (value: unknown): value is Language => value === 'en' || value === 'vi';

/** The language a first-time visitor's device asks for: Vietnamese when it is set to Vietnamese, else English. */
export function detectLanguage(preferred: readonly string[] | undefined): Language {
  const first = preferred?.[0]?.toLowerCase() ?? '';
  return first === 'vi' || first.startsWith('vi-') ? 'vi' : 'en';
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const fill = (template: string, vars: Record<string, string | number | null | undefined>) =>
  template.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name] ?? '') : whole));
const patternCache = new Map<Language, { regex: RegExp; template: string; names: string[] }[]>();

/** Sentences that arrive already filled in (from the server) matched against the dictionary's `{name}` keys. */
function patternsFor(language: Language) {
  let list = patternCache.get(language);
  if (!list) {
    list = [];
    for (const [key, template] of Object.entries(DICTIONARIES[language])) {
      if (!/\{\w+\}/.test(key)) continue;
      const names: string[] = [];
      const source = key
        .split(/\{(\w+)\}/)
        .map((part, index) => {
          if (index % 2 === 0) return escapeRegExp(part);
          names.push(part);
          return '(.+?)';
        })
        .join('');
      list.push({ regex: new RegExp(`^${source}$`, 's'), template, names });
    }
    patternCache.set(language, list);
  }
  return list;
}

/** Text that is already filled in (a server message): translated through the `{name}` keys when one fits. */
export function translateDynamic(language: Language, text: string): string {
  if (language === 'en') return text;
  if (DICTIONARIES[language][text] !== undefined) return DICTIONARIES[language][text];
  for (const { regex, template, names } of patternsFor(language)) {
    const match = regex.exec(text);
    if (match) return fill(template, Object.fromEntries(names.map((n, i) => [n, match[i + 1]])));
  }
  return text;
}

/**
 * The text in `language`: the English `text` itself when there is no translation (so a screen that has not been
 * translated yet still reads fine), with `{name}`-style placeholders filled from `vars`.
 */
export function translate(
  language: Language,
  text: string,
  vars?: Record<string, string | number | null | undefined>,
): string {
  const found = DICTIONARIES[language][text];
  if (found === undefined && !vars && language !== 'en') return translateDynamic(language, text);
  const template = found ?? text;
  return vars
    ? template.replace(/\{(\w+)\}/g, (whole, name: string) =>
        name in vars ? String(vars[name] ?? '') : whole,
      )
    : template;
}
